import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, SquareTerminal } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/layout/theme';
import { useLogin } from '@/hooks/use-auth';
import { useAuthStore } from '@/lib/auth-store';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';

const loginSchema = z.object({
  orgSlug: z
    .string()
    .min(2, 'Required')
    .max(63)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, digits, and dashes only'),
  email: z.string().email('Invalid email'),
  password: z.string().min(1, 'Required'),
});

type LoginValues = z.infer<typeof loginSchema>;

const searchSchema = z.object({ redirect: z.string().optional() });

export const Route = createFileRoute('/login')({
  validateSearch: (search) => searchSchema.parse(search),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/login' });
  const login = useLogin();
  const existingOrg = useAuthStore((s) => s.orgSlug);
  const isAlreadyAuthed = useAuthStore((s) => !!s.refreshToken);

  // If user is already logged in, push them straight to the app.
  useEffect(() => {
    if (isAlreadyAuthed) {
      navigate({ to: redirect ?? '/dashboard' });
    }
  }, [isAlreadyAuthed, navigate, redirect]);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { orgSlug: existingOrg ?? '', email: '', password: '' },
  });

  function onSubmit(values: LoginValues): void {
    login.mutate(values, {
      onSuccess: () => navigate({ to: redirect ?? '/dashboard' }),
      onError: (err) => {
        const message =
          err instanceof ApiError ? err.message : 'Unable to sign in. Please try again.';
        toast({ variant: 'destructive', title: 'Sign-in failed', description: message });
      },
    });
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar with just the theme toggle */}
      <div className="flex justify-end p-4">
        <ThemeToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Editorial brand block */}
          <div className="mb-10 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-4">
              <SquareTerminal className="h-6 w-6" />
            </div>
            <h1 className="font-display text-4xl italic tracking-tight">WeRemote</h1>
            <p className="mt-1 font-mono text-2xs uppercase tracking-[0.3em] text-muted-foreground">
              support console
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Sign in</CardTitle>
              <CardDescription>Sign in to your organization's WeRemote console.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgSlug">Organization</Label>
                  <Input
                    id="orgSlug"
                    autoComplete="organization"
                    placeholder="acme"
                    autoFocus
                    {...form.register('orgSlug')}
                  />
                  {form.formState.errors.orgSlug && (
                    <p className="text-xs text-destructive">{form.formState.errors.orgSlug.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@acme.example"
                    {...form.register('email')}
                  />
                  {form.formState.errors.email && (
                    <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    {...form.register('password')}
                  />
                  {form.formState.errors.password && (
                    <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={login.isPending}>
                  {login.isPending && <Loader2 className="animate-spin" />}
                  Sign in
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center font-mono text-2xs uppercase tracking-widest text-muted-foreground">
            self-hosted · AGPLv3
          </p>
        </div>
      </div>
    </div>
  );
}
